import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { AxiosError } from 'axios'
import { REGEXP_ONLY_DIGITS } from 'input-otp'
import { toast } from 'sonner'
import { Card, CardContent } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp'
import { Label } from '@/components/ui/label'
import { useSession } from '@/shared/hooks/useSession'
import { useSendEmailVerificationCode, useVerifyEmailWithCode } from '@/shared/hooks/api/user'

const VERIFICATION_CODE_LENGTH = 6

type EmailVerificationPageProps = {
  initialState: { verificationCode: string }
  next: (payload: { verificationCode: string }) => void
}

export function EmailVerificationPage({
  initialState,
  next,
}: EmailVerificationPageProps) {
  const { t } = useTranslation()
  const [resendCodeButtonDisabled, setResendCodeButtonDisabled] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [verificationCode, setVerificationCode] = useState(
    initialState.verificationCode
  )
  const [verificationError, setVerificationError] = useState<string | null>(null)
  const {
    mutateAsync: sendEmailVerificationCode,
    isPending: isSendingEmailVerificationCode,
  } = useSendEmailVerificationCode()
  const { mutateAsync: verifyEmailWithCode, isPending: isVerifyingEmailWithCode } =
    useVerifyEmailWithCode()
  const isLoading = isVerifyingEmailWithCode || isSendingEmailVerificationCode
  const {
    session: { email, isEmailVerified },
  } = useSession()
  const isInvalid = verificationError !== null
  const isCodeComplete = verificationCode.length === VERIFICATION_CODE_LENGTH

  const handleSendCode = async () => {
    setVerificationError(null)
    try {
      await sendEmailVerificationCode()
      toast.success(t('onboarding:emailVerification.toast.codeSent'))
      setResendCodeButtonDisabled(true)
      setCountdown(30)
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer)
            setResendCodeButtonDisabled(false)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    } catch (error) {
      toast.error(t('onboarding:emailVerification.toast.genericError'))
      console.error('Error sending verification code:', error)
    }
  }

  const handleVerifyCode = async () => {
    if (isEmailVerified) {
      next({
        verificationCode,
      })
      return
    }
    setVerificationError(null)
    try {
      await verifyEmailWithCode(verificationCode)
      next({
        verificationCode,
      })
    } catch (error) {
      let errorMessage = t('onboarding:emailVerification.genericError')
      if (error instanceof AxiosError) {
        const message = error.response?.data?.message as string | undefined
        if (message === 'INVALID_VERIFICATION_CODE') {
          errorMessage = t('onboarding:emailVerification.invalidCodeError')
        } else if (message === 'VERIFICATION_CODE_EXPIRED') {
          errorMessage = t('onboarding:emailVerification.expiredCodeError')
        }
      }
      setVerificationError(errorMessage)
    }
  }

  const handleVerificationCodeChange = (value: string) => {
    setVerificationCode(value)
    setVerificationError(null)
  }

  return (
    <>
      <Card className="w-full">
        <CardContent className="space-y-6">
          {!isEmailVerified ? (
            <div className="flex flex-col items-center gap-6 pt-2">
              <p className="max-w-sm text-center text-sm leading-relaxed text-muted-foreground">
                <Trans
                  i18nKey="onboarding:emailVerification.sentTo"
                  values={{ email }}
                  components={{
                    email: (
                      <span className="font-medium text-foreground" />
                    ),
                  }}
                />
              </p>

              <div className="flex w-full flex-col items-center gap-4">
                <Label htmlFor="verification-code" className="sr-only">
                  {t('onboarding:emailVerification.codeInputLabel')}
                </Label>
                <InputOTP
                  id="verification-code"
                  maxLength={VERIFICATION_CODE_LENGTH}
                  pattern={REGEXP_ONLY_DIGITS}
                  value={verificationCode}
                  onChange={handleVerificationCodeChange}
                  disabled={isLoading}
                  aria-invalid={isInvalid}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} aria-invalid={isInvalid} />
                    <InputOTPSlot index={1} aria-invalid={isInvalid} />
                    <InputOTPSlot index={2} aria-invalid={isInvalid} />
                    <InputOTPSlot index={3} aria-invalid={isInvalid} />
                    <InputOTPSlot index={4} aria-invalid={isInvalid} />
                    <InputOTPSlot index={5} aria-invalid={isInvalid} />
                  </InputOTPGroup>
                </InputOTP>

                <div className="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-sm text-muted-foreground">
                  <span>{t('onboarding:emailVerification.didntReceiveCode')}</span>
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto px-0"
                    onClick={handleSendCode}
                    disabled={isLoading || resendCodeButtonDisabled}
                  >
                    {resendCodeButtonDisabled
                      ? t('onboarding:emailVerification.actions.resendCodeIn', {
                          countdown,
                        })
                      : t('onboarding:emailVerification.actions.resendCode')}
                  </Button>
                </div>
              </div>

              {verificationError && (
                <Alert variant="destructive" className="w-full">
                  <AlertDescription>{verificationError}</AlertDescription>
                </Alert>
              )}
            </div>
          ) : (
            <Alert variant="success">
              <AlertDescription>{t('onboarding:emailVerification.success')}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
      <div className="mt-6 flex justify-end">
        <Button
          onClick={handleVerifyCode}
          disabled={isLoading || (!isEmailVerified && !isCodeComplete)}
        >
          {t('common:next')}
        </Button>
      </div>
    </>
  )
}
